import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Wrench } from 'lucide-react';
import { Badge, Card, CardContent } from '@scio3/ui';
import { formatDateTimeCompact } from '@/lib/format-date';
import { useOptibotStore } from '../stores/optibot-store';
import { ConnState, type DeviceSessionOutput } from '../hooks/useDeviceSessionStream';

/** Compact in-chat pointer to a device-troubleshoot session. Keeps the chat readable — the full live
 *  timeline + steer composer open in the roomy focused panel. Shows a live status badge (reported by the
 *  session view) and auto-opens the freshly-started session so the user lands in the live view. */
export function DeviceSessionCard({
  session,
  deviceLabel,
  historyOnly = false,
}: {
  session: DeviceSessionOutput;
  deviceLabel?: string;
  /** The "already active" reuse: this card points at a live session we hold NO token for (empty streamUrl).
   *  Open it as history (the focused pane reconnects → resume mints a fresh token) and never auto-open. */
  historyOnly?: boolean;
}) {
  const { t } = useTranslation();
  const openSession = useOptibotStore((s) => s.openSession);
  const conn = useOptibotStore((s) => s.sessionConn[session.sessionId]);
  // Open live only while the session is actually connected; an ended/cold-reloaded session loads its
  // recorded transcript (history) instead of trying — and failing — to reconnect a dead SSE.
  const open = () => {
    const isLive =
      !historyOnly &&
      (conn === ConnState.Live || conn === ConnState.Connecting || conn === ConnState.Reconnecting);
    openSession({ session, mode: isLive ? 'live' : 'history', deviceLabel });
  };

  // Auto-open ONLY when the user just APPROVED starting this session (sessionStartApproved flag). The
  // approval isn't replayed on reload, so cached cards never auto-open; consume the flag + guard once.
  useEffect(() => {
    const store = useOptibotStore.getState();
    if (!store.sessionStartApproved) return;
    store.setSessionStartApproved(false); // consume in all cases so it never leaks to a later card
    if (historyOnly) return; // already-active card: open only on an explicit click, never auto
    if (store.autoOpened[session.sessionId]) return;
    store.markAutoOpened(session.sessionId);
    store.openSession({ session, mode: 'live', deviceLabel });
    // mount-only: session/deviceLabel captured at first render of the new session card
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      data-testid="device-session-card"
      className="my-2 w-full min-w-0 cursor-pointer overflow-hidden transition-colors hover:border-primary/40"
      onClick={open}
    >
      <CardContent className="p-3">
        {/* Row 1: device/title + live status badge. line-clamp (not truncate) so the title can wrap →
            its min-content is just the longest word, not the full string (which would force the card wide). */}
        <div className="flex min-w-0 items-center gap-2">
          <Wrench className="h-4 w-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1 line-clamp-1 text-sm font-medium">
            {deviceLabel ?? t('optibot.session.title')}
          </span>
          {conn && <ConnBadge conn={conn} />}
        </div>
        {/* Row 2: hint (or the start time, for a rebuilt history card → disambiguates sibling sessions on the
            same thread) + the Open affordance BELOW the title so it fits the narrow panel (whole card clickable) */}
        <div className="mt-1 flex min-w-0 items-center justify-between gap-2">
          <span className="min-w-0 line-clamp-1 text-xs text-muted-foreground">
            {session.startedAt
              ? formatDateTimeCompact(session.startedAt)
              : t('optibot.session.cardHint')}
          </span>
          <span className="flex shrink-0 items-center text-xs font-medium text-primary">
            {t('optibot.session.open')}
            <ChevronRight className="ml-0.5 h-3.5 w-3.5" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function ConnBadge({ conn }: { conn: ConnState }) {
  const { t } = useTranslation();
  const variant =
    conn === ConnState.Live ? 'default' : conn === ConnState.Error ? 'destructive' : 'secondary';
  return (
    <Badge variant={variant} className="shrink-0 text-[10px]">
      {t(`optibot.session.conn.${conn}`)}
    </Badge>
  );
}
